import React from 'react';
import Button from './Button';
import Input from './Input';
import { formatDateLongInTimezone, formatTimeInTimezone, getBrowserTimezone } from '../utils/timezone';

interface RenterDetailRequirement {
  fieldName: string;
  description: string;
  required: boolean;
}

interface TimeSlot {
  windowId: string;
  windowName?: string;
  windowType: 'daily' | 'weekly' | 'monthly';
  startDateTime: string;
  endDateTime: string;
  price: number;
  availableQuantity: number;
  totalQuantity: number;
}

interface BookingDetailsSectionProps {
  listing: {
    renterDetailRequirements: RenterDetailRequirement[];
    timezone: string;
  };
  selectedSlots: Set<string>;
  getSelectedSlotObjects: TimeSlot[];
  totalPrice: number;
  renterDetails: Record<string, string>;
  setRenterDetails: (details: Record<string, string>) => void;
  handleAddSelectedToCart: () => void;
  isBooking: boolean;
  clearAllSelections?: () => void;
  isPreview?: boolean;
}

const BookingDetailsSection: React.FC<BookingDetailsSectionProps> = ({
  listing,
  selectedSlots,
  getSelectedSlotObjects,
  totalPrice,
  renterDetails,
  setRenterDetails,
  handleAddSelectedToCart,
  isBooking,
  clearAllSelections: _clearAllSelections,
  isPreview = false,
}) => {
  if (selectedSlots.size === 0) {
    return null;
  }

  // A slot's start/end instant means whatever it means in the listing's
  // timezone, not the renter's browser timezone.
  const timezone = listing.timezone || getBrowserTimezone();

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Booking Details</h2>
      
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Selected Time Slots ({selectedSlots.size})</h3>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {getSelectedSlotObjects.map((slot, idx) => (
            <div key={idx} className="text-sm">
              <p className="text-gray-900 font-medium">
                {formatDateLongInTimezone(new Date(slot.startDateTime), timezone)}
              </p>
              {slot.windowType === 'daily' && (
                <p className="text-gray-600">
                  {formatTimeInTimezone(new Date(slot.startDateTime), timezone)} - {formatTimeInTimezone(new Date(slot.endDateTime), timezone)} • ${slot.price}
                </p>
              )}
            </div>
          ))}
        </div>
        {isPreview && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-lg font-bold text-gray-900">
              Total: ${totalPrice.toFixed(2)}
            </p>
          </div>
        )}
      </div>

      {!isPreview && listing.renterDetailRequirements.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
          <div className="space-y-4 mb-6">
            {listing.renterDetailRequirements.map((req, index) => (
              <div key={index}>
                <Input
                  id={`renter-detail-${req.fieldName.toLowerCase().replace(/\s+/g, '-')}`}
                  label={req.fieldName + (req.required ? ' *' : '')}
                  value={renterDetails[req.fieldName] || ''}
                  onChange={(e) =>
                    setRenterDetails({
                      ...renterDetails,
                      [req.fieldName]: e.target.value,
                    })
                  }
                  required={req.required}
                  helperText={req.description}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {isPreview ? (
        <>
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
            <p className="text-yellow-800 text-sm">
              Preview Mode - Booking is disabled
            </p>
          </div>

          <Button
            disabled={true}
            className="w-full"
            size="lg"
          >
            Preview Mode - Cannot Add to Cart
          </Button>
        </>
      ) : (
        <Button
          onClick={handleAddSelectedToCart}
          isLoading={isBooking}
          disabled={selectedSlots.size === 0}
          className="w-full"
          size="lg"
        >
          Add {selectedSlots.size} Slot{selectedSlots.size !== 1 ? 's' : ''} to Cart - ${totalPrice.toFixed(2)}
        </Button>
      )}
    </div>
  );
};

export default BookingDetailsSection;

